import { BenefitBackground, HeadlineVariation } from '@allesauto/utils/consts';
import {
  FieldTypeAsset,
  FieldTypeHTML,
  FieldTypeLink,
} from '@allesauto/utils/consts';
import {
  createMarkup,
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { renderRichText } from '@storyblok/react';
import { sbEditable } from '@storyblok/storyblok-editable';
import React from 'react';

import CheckList from '../checklistContainer/checklist/CheckList';
import Headline from '../headline';
import NextImage from '../nextImage';
import TextLink from '../textLink';

export interface StepComponentIntroProps {
  headline: string;
  subHeadline?: string;
  upperText?: string;
  upperTextHtml?: any;
  underText?: string;
  checklist?: string[];
  textLinkText?: string;
  textLinkHref?: FieldTypeLink;
  imageLink?: FieldTypeAsset;
  link?: FieldTypeLink;
  component: string;
  _uid: string;
  _editable?: string;
}

const StepComponentIntro: React.FC<StepComponentIntroProps> = (
  props: StepComponentIntroProps
) => {
  const {
    headline,
    subHeadline,
    upperText,
    upperTextHtml,
    underText,
    checklist,
    textLinkText,
    textLinkHref,
    imageLink,
  } = props;

  const url = textLinkHref && getUrlFromStoryblokLink(textLinkHref);
  const stepComponentIntroLink = (url && parseSlashToURL(url)) || '';
  const imageUrl = imageLink?.filename ?? null;

  return (
    <div
      {...sbEditable(props)}
      className="grid grid-cols-24 mb-40px sm:pt-[50px] md:pt-100px"
    >
      <div className="col-start-2 sm:col-end-24 sm:pb-[50px] md:col-end-12 md:pb-0">
        <Headline
          headlineTag={HeadlineVariation.H2}
          headlineText={headline}
          headlineColor="text-black"
        />
      </div>
      <div className="col-end-24 mb-40px sm:col-start-2 md:col-start-14">
        {upperTextHtml ? (
          <p
            className="text-grey80"
            dangerouslySetInnerHTML={createMarkup(
              renderRichText(upperTextHtml as FieldTypeHTML)
            )}
          ></p>
        ) : (
          <p className="text-grey80">{upperText}</p>
        )}
      </div>
      {imageUrl && (
        <div className="col-start-2 sm:col-end-24 sm:mb-20px md:col-end-12 md:mb-0">
          <div className="aspect-[16/10] relative">
            <NextImage
              baseUrls={
                process.env.IMAGE_BASE_URL ||
                process.env.NEXT_PUBLIC_IMAGE_BASE_URL ||
                ''
              }
              alt={imageLink?.alt}
              layout="fill"
              src={imageUrl}
              objectFit="cover"
              loader={({ src, width, quality }) => {
                return `${src}?w=${width}&q=${quality || 75}`;
              }}
            />
          </div>
        </div>
      )}
      <div className="col-end-24 flex flex-col leading-6 sm:col-start-2 md:col-start-14">
        {subHeadline && (
          <p className="font-headline mb-20px text-grey100">{subHeadline}</p>
        )}
        {underText && <p className="mb-20px text-grey80">{underText}</p>}
        {checklist && (
          <CheckList
            list={{
              listItems: checklist,
              background: BenefitBackground.WHITE,
              customClassName: {
                wrapperClassName: '!p-0',
              },
            }}
          />
        )}
        <div className="flex sm:flex-col md:flex-row">
          {textLinkText && textLinkHref && (
            <TextLink
              text={textLinkText}
              href={parseSlashToURL(stepComponentIntroLink)}
              customClassName={'pr-[32px] sm:pb-15px md:pb-0'}
              key={`step-component-intro-text-link`}
              target={textLinkHref?.target}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StepComponentIntro;
